/**
 * Shopee Style Vouchers JavaScript with Auto Apply
 * File: assets/shopee-vouchers.js
 */

(function() {
  'use strict';

  // Voucher Manager Class
  class ShopeeVoucherManager {
    constructor() {
      this.storageKey = 'shopee_claimed_vouchers';
      this.appliedVoucherKey = 'shopee_applied_voucher';
      this.claimedVouchers = this.getClaimedVouchers();
      this.productPrice = parseFloat(document.querySelector('.shopee-vouchers-container')?.dataset.productPrice || 0);
      this.init();
    }

    init() {
      this.bindEvents();
      this.checkClaimedVouchers();
      this.updateValidDates();
      this.checkAppliedVoucher();
      this.autoCheckBestVoucher();
    }

    bindEvents() {
      // Desktop hover/click events
      document.querySelectorAll('.voucher-item').forEach(item => {
        const card = item.querySelector('.voucher-card');
        const claimBtn = item.querySelector('.voucher-claim-btn');
        const dropdownClaimBtn = item.querySelector('.dropdown-claim-btn');

        // Desktop: Show dropdown on hover
        if (window.innerWidth > 768) {
          item.addEventListener('mouseenter', () => this.showDropdown(item));
          item.addEventListener('mouseleave', () => this.hideDropdown(item));
        }

        // Mobile: Show dropdown on click
        card.addEventListener('click', (e) => {
          if (window.innerWidth <= 768 && !e.target.closest('.voucher-claim-btn')) {
            this.toggleDropdownMobile(item);
          }
        });

        // Claim button events
        claimBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.claimVoucher(item);
        });

        if (dropdownClaimBtn) {
          dropdownClaimBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.claimVoucher(item);
            this.hideDropdown(item);
          });
        }
      });

      // View more button
      const viewMoreBtn = document.querySelector('.view-more-vouchers');
      if (viewMoreBtn) {
        viewMoreBtn.addEventListener('click', () => this.viewMoreVouchers());
      }

      // Close mobile dropdown on overlay click
      document.addEventListener('click', (e) => {
        if (window.innerWidth <= 768 && !e.target.closest('.voucher-item')) {
          this.closeAllDropdowns();
        }
      });

      // Listen for cart updates
      document.addEventListener('cart:updated', () => {
        this.autoCheckBestVoucher();
      });
    }

    updateValidDates() {
      document.querySelectorAll('.voucher-item').forEach(item => {
        const validDays = parseInt(item.dataset.validDays);
        const validUntil = new Date();
        validUntil.setDate(validUntil.getDate() + validDays);
        
        const dateElements = item.querySelectorAll('.valid-until-date');
        dateElements.forEach(el => {
          el.textContent = validUntil.toLocaleDateString('en-MY', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          });
        });
      });
    }

    showDropdown(item) {
      item.classList.add('show-dropdown');
    }

    hideDropdown(item) {
      item.classList.remove('show-dropdown');
    }

    toggleDropdownMobile(item) {
      const isOpen = item.classList.contains('show-dropdown');
      
      // Close all other dropdowns
      this.closeAllDropdowns();
      
      if (!isOpen) {
        item.classList.add('show-dropdown');
        
        // Create overlay for mobile
        if (window.innerWidth <= 768) {
          this.createOverlay();
        }
      }
    }

    closeAllDropdowns() {
      document.querySelectorAll('.voucher-item').forEach(item => {
        item.classList.remove('show-dropdown');
      });
      this.removeOverlay();
    }

    createOverlay() {
      if (!document.querySelector('.voucher-overlay')) {
        const overlay = document.createElement('div');
        overlay.className = 'voucher-overlay';
        document.body.appendChild(overlay);
        
        setTimeout(() => overlay.classList.add('active'), 10);
        
        overlay.addEventListener('click', () => this.closeAllDropdowns());
      }
    }

    removeOverlay() {
      const overlay = document.querySelector('.voucher-overlay');
      if (overlay) {
        overlay.classList.remove('active');
        setTimeout(() => overlay.remove(), 300);
      }
    }

    claimVoucher(item) {
      const voucherId = item.dataset.voucherId;
      const voucherData = {
        id: voucherId,
        value: parseFloat(item.dataset.voucherValue),
        type: item.dataset.voucherType,
        minSpend: parseFloat(item.dataset.minSpend),
        validDays: parseInt(item.dataset.validDays),
        maxUses: parseInt(item.dataset.maxUses),
        claimedAt: new Date().getTime(),
        usedCount: 0,
        text: item.querySelector('.voucher-discount').textContent
      };

      // Check if already claimed
      if (this.isVoucherClaimed(voucherId)) {
        this.showNotification('This voucher has already been claimed!', 'error');
        return;
      }

      // Add claiming state
      item.classList.add('voucher-claiming');

      // Simulate API call
      setTimeout(() => {
        // Save to storage
        this.saveClaimedVoucher(voucherData);
        
        // Update UI
        item.classList.remove('voucher-claiming');
        item.classList.add('claimed');
        
        // Show success notification
        this.showNotification('Voucher claimed successfully!', 'success');
        
        // Auto apply if cart meets conditions
        this.autoApplyVoucher(voucherData);
      }, 500);
    }

    saveClaimedVoucher(voucherData) {
      this.claimedVouchers.push(voucherData);
      localStorage.setItem(this.storageKey, JSON.stringify(this.claimedVouchers));
    }

    getClaimedVouchers() {
      try {
        const stored = localStorage.getItem(this.storageKey);
        return stored ? JSON.parse(stored) : [];
      } catch (e) {
        return [];
      }
    }

    isVoucherClaimed(voucherId) {
      return this.claimedVouchers.some(v => v.id === voucherId);
    }

    checkClaimedVouchers() {
      // Remove expired vouchers
      this.claimedVouchers = this.claimedVouchers.filter(voucher => {
        const expiryTime = voucher.claimedAt + (voucher.validDays * 24 * 60 * 60 * 1000);
        return new Date().getTime() < expiryTime;
      });
      
      // Update storage
      localStorage.setItem(this.storageKey, JSON.stringify(this.claimedVouchers));
      
      // Update UI
      this.claimedVouchers.forEach(voucher => {
        const item = document.querySelector(`[data-voucher-id="${voucher.id}"]`);
        if (item && voucher.usedCount < voucher.maxUses) {
          item.classList.add('claimed');
        }
      });
    }

    checkAppliedVoucher() {
      const appliedVoucher = this.getAppliedVoucher();
      if (appliedVoucher) {
        const item = document.querySelector(`[data-voucher-id="${appliedVoucher.id}"]`);
        if (item) {
          item.classList.add('applied');
        }
      }
    }

    getAppliedVoucher() {
      try {
        const stored = localStorage.getItem(this.appliedVoucherKey);
        return stored ? JSON.parse(stored) : null;
      } catch (e) {
        return null;
      }
    }

    autoCheckBestVoucher() {
      // Get current cart total
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const cartTotal = cart.total_price / 100; // Convert from cents
          
          // Find best applicable voucher
          const applicableVouchers = this.claimedVouchers.filter(voucher => {
            const isExpired = new Date().getTime() > (voucher.claimedAt + (voucher.validDays * 24 * 60 * 60 * 1000));
            const meetsMinSpend = cartTotal >= voucher.minSpend;
            const hasUsesLeft = voucher.usedCount < voucher.maxUses;
            
            return !isExpired && meetsMinSpend && hasUsesLeft;
          });

          if (applicableVouchers.length > 0) {
            // Sort by discount value (highest first)
            const bestVoucher = applicableVouchers.sort((a, b) => {
              const aDiscount = this.calculateDiscount(a, cartTotal);
              const bDiscount = this.calculateDiscount(b, cartTotal);
              return bDiscount - aDiscount;
            })[0];

            // Auto apply best voucher
            if (!this.getAppliedVoucher() || this.getAppliedVoucher().id !== bestVoucher.id) {
              this.applyVoucherToCart(bestVoucher);
            }
          }
        });
    }

    calculateDiscount(voucher, cartTotal) {
      if (voucher.type === 'percentage') {
        return cartTotal * (voucher.value / 100);
      } else if (voucher.type === 'fixed') {
        return voucher.value;
      } else if (voucher.type === 'shipping') {
        // Assume shipping cost
        return 10; // Default shipping cost
      } else if (voucher.type === 'cashback') {
        return cartTotal * (voucher.value / 100);
      }
      return 0;
    }

    autoApplyVoucher(voucherData) {
      // Check if cart meets minimum spend
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const cartTotal = cart.total_price / 100; // Convert from cents
          
          if (cartTotal >= voucherData.minSpend) {
            this.applyVoucherToCart(voucherData);
          } else {
            this.showNotification(`Add ${(voucherData.minSpend - cartTotal).toFixed(2)} more to use this voucher!`, 'info');
          }
        });
    }

    applyVoucherToCart(voucherData) {
      // Calculate discount
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const cartTotal = cart.total_price / 100;
          const discount = this.calculateDiscount(voucherData, cartTotal);
          
          // Apply voucher to cart via cart attributes
          const formData = {
            attributes: {
              'Shopee_Voucher_ID': voucherData ? voucherData.id : '',
              'Shopee_Voucher_Text': voucherData ? voucherData.text : '',
              'Shopee_Voucher_Value': voucherData ? voucherData.value : '',
              'Shopee_Voucher_Type': voucherData ? voucherData.type : '',
              'Shopee_Voucher_Discount': discount.toFixed(2)
            }
          };

          return fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData)
          });
        })
        .then(response => response.json())
        .then(data => {
          if (voucherData) {
            localStorage.setItem(this.appliedVoucherKey, JSON.stringify(voucherData));
            this.showNotification('Voucher applied to cart!', 'success');
            
            // Update UI
            document.querySelectorAll('.voucher-item').forEach(item => {
              item.classList.remove('applied');
            });
            const appliedItem = document.querySelector(`[data-voucher-id="${voucherData.id}"]`);
            if (appliedItem) {
              appliedItem.classList.add('applied');
            }
            
            // Update mini cart if exists
            if (window.updateMiniCart) {
              window.updateMiniCart();
            }
            
            // Refresh cart page if on cart
            if (window.location.pathname === '/cart') {
              location.reload();
            }
          }
        });
    }

    viewMoreVouchers() {
      // Redirect to vouchers page or open modal
      window.location.href = '/pages/shop-vouchers';
    }

    showNotification(message, type = 'info') {
      // Create notification element
      const notification = document.createElement('div');
      notification.className = `voucher-notification ${type}`;
      notification.innerHTML = `
        <div class="notification-content">
          <span>${message}</span>
          <button class="notification-close">&times;</button>
        </div>
      `;
      
      document.body.appendChild(notification);
      
      // Auto remove after 3 seconds
      setTimeout(() => {
        notification.classList.add('fade-out');
        setTimeout(() => notification.remove(), 300);
      }, 3000);
      
      // Close on click
      notification.querySelector('.notification-close').addEventListener('click', () => {
        notification.remove();
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new ShopeeVoucherManager();
    });
  } else {
    new ShopeeVoucherManager();
  }

  // Notification styles
  const style = document.createElement('style');
  style.textContent = `
    .voucher-notification {
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #333;
      color: #fff;
      padding: 16px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      z-index: 9999;
      animation: slideIn 0.3s ease;
      max-width: 300px;
    }
    
    .voucher-notification.success {
      background: #4CAF50;
    }
    
    .voucher-notification.error {
      background: #F44336;
    }
    
    .voucher-notification.info {
      background: #2196F3;
    }
    
    .notification-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }
    
    .notification-close {
      background: none;
      border: none;
      color: #fff;
      font-size: 20px;
      cursor: pointer;
      padding: 0;
      line-height: 1;
    }
    
    .voucher-notification.fade-out {
      animation: slideOut 0.3s ease;
    }
    
    @keyframes slideIn {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }
    
    @keyframes slideOut {
      from {
        transform: translateX(0);
        opacity: 1;
      }
      to {
        transform: translateX(100%);
        opacity: 0;
      }
    }
    
    @media (max-width: 768px) {
      .voucher-notification {
        bottom: 10px;
        right: 10px;
        left: 10px;
        max-width: none;
      }
    }
  `;
  document.head.appendChild(style);
})();